import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * TemplateRow — matches exact RPC return shape (15 snake_case columns).
 * Do NOT map to camelCase — avoids mapping layer bugs and matches RPC contract.
 */
export interface TemplateRow {
  id: string;
  template_name: string;
  template_id: string;
  language: string;
  category: string;
  status: string;
  components: unknown;
  example: unknown;
  quality_score: unknown;
  created_at: string;
  updated_at: string;
  api_configuration_id: string;
  phone_number: string;
  account_display_name: string;
  rejection_reason: string | null;
  source_content?: Record<string, unknown> | null;
}

interface UseWhatsAppTemplatesOptions {
  statusFilter?: string | null;
  enabled?: boolean;
}

const QUERY_KEY = 'whatsapp-templates';

export function useWhatsAppTemplates(options: UseWhatsAppTemplatesOptions = {}) {
  const { tenantData } = useAuth();
  const { statusFilter = null, enabled = true } = options;

  return useQuery<TemplateRow[]>({
    queryKey: [QUERY_KEY, statusFilter, tenantData?.schema_name],
    queryFn: async ({ signal }) => {
      const { data, error } = await supabase
        .rpc('get_tenant_whatsapp_templates', {
          status_filter: statusFilter,
        })
        .abortSignal(signal);

      if (error) {
        throw error;
      }

      return (data as TemplateRow[]) || [];
    },
    enabled: enabled && !!tenantData?.schema_name,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    placeholderData: keepPreviousData,
  });
}

export function useSyncTemplates() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (accountId: string) => {
      const { data, error } = await supabase.functions.invoke('whatsapp-template-sync', {
        body: { accountId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    retry: 0,
  });

  return { ...mutation, isSyncing: mutation.isPending };
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (params: {
      accountId: string;
      name: string;
      category: string;
      language: string;
      components: unknown[];
      sourceContent?: Record<string, unknown>;
      examples?: Record<string, string>;
    }) => {
      const { data, error } = await supabase.functions.invoke('whatsapp-template-create', {
        body: {
          action: 'create',
          accountId: params.accountId,
          name: params.name,
          category: params.category,
          language: params.language,
          components: params.components,
          ...(params.sourceContent ? { sourceContent: params.sourceContent } : {}),
          ...(params.examples ? { examples: params.examples } : {}),
        },
      });
      if (error) {
        // FunctionsHttpError.context is the raw Response — parse body for Meta error details
        let userMsg: string | undefined;
        try {
          const body = await (error as Record<string, unknown>)?.context?.json();
          userMsg = body?.error_user_msg;
        } catch {
          // Body already consumed or not JSON — ignore
        }
        if (userMsg) throw new Error(userMsg);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    retry: 0,
  });

  return { ...mutation, isCreating: mutation.isPending };
}

export function useEditTemplate() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (params: {
      accountId: string;
      templateId: string;
      components: unknown[];
      sourceContent?: Record<string, unknown>;
      examples?: Record<string, string>;
    }) => {
      const { data, error } = await supabase.functions.invoke('whatsapp-template-create', {
        body: {
          action: 'edit',
          accountId: params.accountId,
          templateId: params.templateId,
          components: params.components,
          ...(params.sourceContent ? { sourceContent: params.sourceContent } : {}),
          ...(params.examples ? { examples: params.examples } : {}),
        },
      });
      if (error) {
        // FunctionsHttpError.context is the raw Response — parse body for Meta error details
        let userMsg: string | undefined;
        try {
          const body = await (error as Record<string, unknown>)?.context?.json();
          userMsg = body?.error_user_msg;
        } catch {
          // Body already consumed or not JSON — ignore
        }
        if (userMsg) throw new Error(userMsg);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    retry: 0,
  });

  return { ...mutation, isEditing: mutation.isPending };
}

export function useCheckTemplateUsage() {
  return useMutation({
    mutationFn: async (templateName: string) => {
      const { data, error } = await supabase.rpc('check_whatsapp_template_usage', {
        p_template_name: templateName,
      });
      if (error) throw error;
      return data as { total: number; workflows: { id: string; name: string }[]; integrations: { id: string; name: string }[] };
    },
  });
}

export function useSaveDraft() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (params: {
      accountId: string;
      name: string;
      category: string;
      language: string;
      components: unknown[];
      draftId?: string;
      sourceContent?: Record<string, unknown>;
      examples?: Record<string, string>;
    }) => {
      const { data, error } = await supabase.functions.invoke('whatsapp-template-create', {
        body: {
          action: 'save_draft',
          accountId: params.accountId,
          name: params.name,
          category: params.category,
          language: params.language,
          components: params.components,
          ...(params.draftId ? { draftId: params.draftId } : {}),
          ...(params.sourceContent ? { sourceContent: params.sourceContent } : {}),
          ...(params.examples ? { examples: params.examples } : {}),
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    retry: 0,
  });

  return { ...mutation, isSavingDraft: mutation.isPending };
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (params: { accountId: string; templateName: string; templateId?: string }) => {
      const { data, error } = await supabase.functions.invoke('whatsapp-template-create', {
        body: {
          action: 'delete',
          accountId: params.accountId,
          templateName: params.templateName,
          ...(params.templateId ? { templateId: params.templateId } : {}),
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    retry: 0,
  });

  return { ...mutation, isDeleting: mutation.isPending };
}
